/**
 * The project's architecture as a picture (docs/plans/2026-10-project-level-work.md, P3):
 * project layers that span the project's repositories, each repository's
 * areas inside the layer that claims them, and the connections between
 * repositories — the one kind of edge a single repository's map cannot hold.
 *
 * Two inputs: the project's own document (`ProjectArchitecturePut`: layers,
 * notes on areas, connections) and what each repository last shared of its
 * own map (`state.map.roots[].architecture`). A repository that has shared
 * nothing yet is still a repository of the project: it is drawn, with
 * nothing in it.
 *
 * DOM-free, and only types come from `shared/` — its schema files pull in
 * zod, which neither the page's model tests nor tsx can load — so the three
 * small rules are restated here. The canonical ones are `parseAreaRef`,
 * `layerMatchScore` and `projectLayerOf` in `shared/schemas/projectWork.ts`;
 * test/projectArchModel.test.ts pins the behaviour they share.
 *
 * The edit helpers take a document and return a new one, so the editor works
 * on a local copy and saves it whole.
 *
 * Ported from the workbench's components/architecture/projectArchModel.ts: the desktop's shell types and its
 * `relativeTime` are restated above; the rest is as the workbench has it.
 */
import type { ProjectArchitecturePut, ProjectArchitectureResponse, ProjectConnection, ProjectConnectionKind, ProjectLayer, ProjectRootMap } from '@aioe/shared';

// The console's half of what the workbench reads from its desktop shell (its protocol/shell.ts): the project's
// repositories and what each last mirrored of its own map (`GET /projects/v1/:id/map`), and where the project is kept.
type ProjectHomeKind = 'private' | 'hub' | 'organisation';
interface ProjectWorkState {
  map: { roots: ReadonlyArray<Pick<ProjectRootMap, 'rootId' | 'architecture' | 'updatedAt'>> };
  repositories: ReadonlyArray<{ rootId: string; name: string; mirroredAt?: string; desktopRootId?: string }>;
  scribe?: { waitingFor: string[]; what: 'roadmap' | 'picture'; since?: string };
}

/** "3 h ago", "yesterday": how long since a time, in words; empty for none. */
function relativeTime(iso: string | undefined, now: number): string {
  const at = iso ? Date.parse(iso) : Number.NaN;
  if (Number.isNaN(at)) return '';
  const m = Math.max(0, Math.round((now - at) / 60_000));
  if (m < 1) return 'just now';
  if (m < 60) return `${m} min ago`;
  const h = Math.round(m / 60);
  if (h < 24) return `${h} h ago`;
  const d = Math.round(h / 24);
  return d === 1 ? 'yesterday' : `${d} days ago`;
}

export type { ProjectArchitecturePut, ProjectConnection, ProjectConnectionKind, ProjectLayer };

/** What the model reads of `ProjectWorkState`: a state is one, and a test can build less. */
export interface ProjectArchInput {
  name?: string;
  architecture: ProjectArchitecturePut;
  map: { roots: ReadonlyArray<Pick<ProjectWorkState['map']['roots'][number], 'rootId' | 'architecture' | 'updatedAt'>> };
  repositories: ReadonlyArray<Pick<ProjectWorkState['repositories'][number], 'rootId' | 'name' | 'mirroredAt' | 'desktopRootId'>>;
}

// ── The three rules (see the header) ───────────────────────────────────

/** `<rootId>:<path>` for a repository's area. */
export function areaRefOf(rootId: string, path: string): string {
  return `${rootId}:${path}`;
}

/**
 * The repository and path a reference names. With the project's repository
 * ids given, the longest one the reference starts with wins (an id may hold
 * a colon); without them, the first colon divides. A bare `<rootId>` is the
 * whole repository (`path: ""`).
 */
export function parseAreaRef(ref: string, rootIds?: readonly string[]): { rootId: string; path: string } {
  if (rootIds) {
    let best: string | undefined;
    for (const r of rootIds) if ((ref === r || ref.startsWith(`${r}:`)) && (!best || r.length > best.length)) best = r;
    if (best !== undefined) return { rootId: best, path: ref.length > best.length ? ref.slice(best.length + 1) : '' };
  }
  const at = ref.indexOf(':');
  return at < 0 ? { rootId: ref, path: '' } : { rootId: ref.slice(0, at), path: ref.slice(at + 1) };
}

/** How well a layer's `match` entry covers an area: 0 when it does not, else higher for the more specific. */
export function layerMatchScore(entry: string, rootId: string, path: string, rootIds?: readonly string[]): number {
  const m = parseAreaRef(entry, rootIds);
  if (m.rootId !== rootId) return 0;
  if (m.path === '*' || m.path === '') return 1;
  if (m.path === path) return 100_000;
  if (m.path.endsWith('/*')) {
    const base = m.path.slice(0, -2);
    if (path === base || path.startsWith(`${base}/`)) return 2 + base.length;
  }
  return 0;
}

/** The project layer an area belongs to: its own note's, else the best match, else none. */
export function projectLayerOf(doc: Pick<ProjectArchitecturePut, 'layers' | 'areas'>, rootId: string, path: string, rootIds?: readonly string[]): string | undefined {
  const noted = doc.areas?.[areaRefOf(rootId, path)]?.layer;
  if (noted && doc.layers.some((l) => l.id === noted)) return noted;
  let best: { id: string; score: number } | undefined;
  for (const layer of doc.layers) {
    for (const entry of layer.match ?? []) {
      const score = layerMatchScore(entry, rootId, path, rootIds);
      if (score > (best?.score ?? 0)) best = { id: layer.id, score };
    }
  }
  return best?.id;
}

// ── The model ──────────────────────────────────────────────────────────

/** Docs, config, assets and tests: part of a repository, not of how the project fits together. */
const SUPPORT_KINDS = new Set(['docs', 'config', 'assets', 'tests']);

export interface ProjectArchArea {
  /** `<rootId>:<path>`: what a note and a connection call it. */
  key: string;
  rootId: string;
  /** Its id in its own repository's map (what `dependsOn` names). */
  id: string;
  path: string;
  /** The project's name for it when it has one, else the repository's. */
  title: string;
  /** What its own repository calls it. */
  repositoryTitle: string;
  description?: string;
  /** The title of the layer it is in, in its own repository. */
  repositoryLayer?: string;
  /** The project layer that claims it. */
  layerId?: string;
  /** Put in its layer by hand (its own note), rather than with its repository. */
  placedByHand: boolean;
  /** Docs, config, assets or tests: drawn only when asked for. */
  support: boolean;
  files?: number;
  symbols?: number;
}

export interface ProjectArchGroup {
  rootId: string;
  name: string;
  /** False for a repository nothing is known of yet. */
  known: boolean;
  areas: ProjectArchArea[];
}

export interface ProjectArchLayer {
  id: string;
  title: string;
  description?: string;
  repositories: ProjectArchGroup[];
}

export interface ProjectArchRepository {
  rootId: string;
  name: string;
  /** Whether it has shared a map with any areas in it. */
  known: boolean;
  areaCount: number;
  mirroredAt?: string;
  /** The desktop's own key for it, when it is on this desktop. */
  desktopRootId?: string;
  /** The layer most of it is in: where an edge to "the whole repository" lands. `null` is "Not placed yet". */
  homeLayer: string | null;
}

export interface ProjectArchEnd {
  /** As written in the document. */
  ref: string;
  rootId: string;
  /** "" for the whole repository. */
  path: string;
  repositoryName: string;
  /** False when no repository of the project has that id. */
  known: boolean;
  /** The area it names, or the nearest area above it; absent for a whole repository, or when nothing there is known. */
  areaId?: string;
  areaKey?: string;
  areaTitle?: string;
  /** True when `areaKey` is the very area named, false when it is a parent (or nothing was found). */
  exact: boolean;
}

export interface ProjectArchConnection {
  /** Its place in the document's `connections`. */
  index: number;
  from: ProjectArchEnd;
  to: ProjectArchEnd;
  kind: ProjectConnectionKind;
  label?: string;
  description?: string;
  /** Both ends name a repository of the project. */
  valid: boolean;
}

/** One repository's own area-to-area link, kept inside that repository. */
export interface ProjectArchInternalEdge {
  rootId: string;
  from: string;
  to: string;
  fromKey: string;
  toKey: string;
  weight: number;
}

export interface ProjectArchModel {
  title: string;
  summary?: string;
  layers: ProjectArchLayer[];
  /** Areas no project layer claims, by repository; a repository nothing is known of and no layer names is here too, empty. */
  unplaced: ProjectArchGroup[];
  repositories: ProjectArchRepository[];
  connections: ProjectArchConnection[];
  internal: ProjectArchInternalEdge[];
  /** Every area by its key. */
  areas: Map<string, ProjectArchArea>;
  counts: { layers: number; repositories: number; areas: number; connections: number };
}

const KINDS: readonly ProjectConnectionKind[] = ['http', 'package', 'protocol', 'data', 'other'];

/** The five kinds of connection, in the words a person chooses between. */
export const CONNECTION_KINDS: ReadonlyArray<{ id: ProjectConnectionKind; label: string; hint: string }> = [
  { id: 'http', label: 'Web request', hint: 'One calls the other over the network' },
  { id: 'package', label: 'Package', hint: 'One uses code the other publishes' },
  { id: 'protocol', label: 'Shared contract', hint: 'Both follow the same message or data shape' },
  { id: 'data', label: 'Data', hint: 'One reads or writes what the other stores' },
  { id: 'other', label: 'Other', hint: 'Something else' },
];

export function connectionKindLabel(kind: ProjectConnectionKind): string {
  return CONNECTION_KINDS.find((k) => k.id === kind)?.label ?? 'Other';
}

/** What an edge says on the picture: the person's own label, else the kind in a word. */
export function connectionCaption(c: Pick<ProjectArchConnection, 'kind' | 'label'>): string {
  return c.label?.trim() || connectionKindLabel(c.kind);
}

/** "web · Checkout", or just the repository for a whole one. */
export function endCaption(end: ProjectArchEnd): string {
  if (!end.path) return end.repositoryName;
  return `${end.repositoryName} · ${end.exact && end.areaTitle ? end.areaTitle : end.path}`;
}

/** The project's repositories in the order the project lists them, then any that only shared a map. */
function repositoriesOf(state: ProjectArchInput): Array<{ rootId: string; name: string; mirroredAt?: string; desktopRootId?: string }> {
  const out = state.repositories.map((r) => ({ rootId: r.rootId, name: r.name || r.rootId, ...(r.mirroredAt ? { mirroredAt: r.mirroredAt } : {}), ...(r.desktopRootId ? { desktopRootId: r.desktopRootId } : {}) }));
  const seen = new Set(out.map((r) => r.rootId));
  for (const root of state.map.roots) {
    if (seen.has(root.rootId)) continue;
    seen.add(root.rootId);
    out.push({ rootId: root.rootId, name: root.rootId, ...(root.updatedAt ? { mirroredAt: root.updatedAt } : {}) });
  }
  return out;
}

/** Every repository id the project knows: what a reference is read against. */
export function repositoryIds(state: ProjectArchInput): string[] {
  return repositoriesOf(state).map((r) => r.rootId);
}

/** The first layer that names the whole repository (`<rootId>:*` or a bare `<rootId>`). */
function wholeRepositoryLayer(doc: Pick<ProjectArchitecturePut, 'layers'>, rootId: string, rootIds: readonly string[]): string | undefined {
  for (const layer of doc.layers) {
    for (const entry of layer.match ?? []) {
      const m = parseAreaRef(entry, rootIds);
      if (m.rootId === rootId && (m.path === '*' || m.path === '')) return layer.id;
    }
  }
  return undefined;
}

export function buildProjectArchModel(state: ProjectArchInput, document: ProjectArchitecturePut = state.architecture): ProjectArchModel {
  const repos = repositoriesOf(state);
  const rootIds = repos.map((r) => r.rootId);
  const layerIds = new Set(document.layers.map((l) => l.id));
  const areas = new Map<string, ProjectArchArea>();
  const byRepository = new Map<string, ProjectArchArea[]>();
  const internal: ProjectArchInternalEdge[] = [];

  for (const repo of repos) {
    const arch = state.map.roots.find((r) => r.rootId === repo.rootId)?.architecture;
    const layerTitle = new Map((arch?.layers ?? []).map((l) => [l.id, l.title]));
    const list: ProjectArchArea[] = [];
    const keyOfId = new Map<string, string>();
    for (const a of arch?.areas ?? []) {
      const key = areaRefOf(repo.rootId, a.path);
      if (areas.has(key)) continue;
      const note = document.areas?.[key];
      const layerId = projectLayerOf(document, repo.rootId, a.path, rootIds);
      const description = note?.description?.trim() || a.description;
      const area: ProjectArchArea = {
        key,
        rootId: repo.rootId,
        id: a.id,
        path: a.path,
        title: note?.title?.trim() || a.title || a.path,
        repositoryTitle: a.title || a.path,
        ...(description ? { description } : {}),
        ...(layerTitle.get(a.layer) ? { repositoryLayer: layerTitle.get(a.layer) } : {}),
        ...(layerId ? { layerId } : {}),
        placedByHand: !!note?.layer && layerIds.has(note.layer),
        support: SUPPORT_KINDS.has(a.kind ?? 'code'),
        ...(typeof a.files === 'number' ? { files: a.files } : {}),
        ...(typeof a.symbols === 'number' ? { symbols: a.symbols } : {}),
      };
      areas.set(key, area);
      keyOfId.set(a.id, key);
      list.push(area);
    }
    byRepository.set(repo.rootId, list);
    for (const a of arch?.areas ?? []) {
      const fromKey = keyOfId.get(a.id);
      for (const dep of a.dependsOn ?? []) {
        const toKey = keyOfId.get(dep.id);
        if (!fromKey || !toKey || fromKey === toKey) continue;
        internal.push({ rootId: repo.rootId, from: a.id, to: dep.id, fromKey, toKey, weight: dep.weight ?? 1 });
      }
    }
  }

  const groupsOf = (layerId: string | undefined): ProjectArchGroup[] => {
    const out: ProjectArchGroup[] = [];
    for (const repo of repos) {
      const all = byRepository.get(repo.rootId) ?? [];
      const mine = all.filter((a) => a.layerId === layerId);
      // A repository with nothing to draw still stands where it was put: in the layer that names it whole, else unplaced.
      const emptyHere = all.length === 0 && wholeRepositoryLayer(document, repo.rootId, rootIds) === layerId;
      if (mine.length || emptyHere) out.push({ rootId: repo.rootId, name: repo.name, known: all.length > 0, areas: mine });
    }
    return out;
  };

  const layers: ProjectArchLayer[] = document.layers.map((l) => ({ id: l.id, title: l.title || l.id, ...(l.description ? { description: l.description } : {}), repositories: groupsOf(l.id) }));
  const unplaced = groupsOf(undefined);

  const repositories: ProjectArchRepository[] = repos.map((repo) => {
    const all = byRepository.get(repo.rootId) ?? [];
    return { rootId: repo.rootId, name: repo.name, known: all.length > 0, areaCount: all.length, ...(repo.mirroredAt ? { mirroredAt: repo.mirroredAt } : {}), ...(repo.desktopRootId ? { desktopRootId: repo.desktopRootId } : {}), homeLayer: homeLayerOf(document, repo.rootId, all, rootIds) };
  });

  const repoById = new Map(repos.map((r) => [r.rootId, r]));
  const resolve = (ref: string): ProjectArchEnd => {
    const parsed = parseAreaRef(ref.trim(), rootIds);
    const path = parsed.path === '*' ? '' : parsed.path.replace(/\/+$/, '');
    const repo = repoById.get(parsed.rootId);
    if (!repo) return { ref, rootId: parsed.rootId, path, repositoryName: parsed.rootId, known: false, exact: false };
    const end: ProjectArchEnd = { ref, rootId: repo.rootId, path, repositoryName: repo.name, known: true, exact: !path };
    if (!path) return end;
    const all = byRepository.get(repo.rootId) ?? [];
    const exact = all.find((a) => a.path === path);
    // Not an area of its own: the nearest area above it stands for it.
    const found = exact ?? all.filter((a) => a.path && path.startsWith(`${a.path}/`)).sort((a, b) => b.path.length - a.path.length)[0];
    return found ? { ...end, areaId: found.id, areaKey: found.key, areaTitle: found.title, exact: !!exact } : end;
  };

  const connections: ProjectArchConnection[] = (document.connections ?? []).map((c, index) => {
    const from = resolve(String(c.from ?? ''));
    const to = resolve(String(c.to ?? ''));
    return { index, from, to, kind: KINDS.includes(c.kind) ? c.kind : 'other', ...(c.label?.trim() ? { label: c.label.trim() } : {}), ...(c.description?.trim() ? { description: c.description.trim() } : {}), valid: from.known && to.known };
  });

  return {
    title: document.title?.trim() || state.name?.trim() || 'Project architecture',
    ...(document.summary?.trim() ? { summary: document.summary.trim() } : {}),
    layers,
    unplaced,
    repositories,
    connections,
    internal,
    areas,
    counts: { layers: layers.length, repositories: repositories.length, areas: areas.size, connections: connections.length },
  };
}

function homeLayerOf(doc: Pick<ProjectArchitecturePut, 'layers'>, rootId: string, areas: readonly ProjectArchArea[], rootIds: readonly string[]): string | null {
  if (!areas.length) return wholeRepositoryLayer(doc, rootId, rootIds) ?? null;
  const counts = new Map<string | null, number>();
  for (const a of areas) counts.set(a.layerId ?? null, (counts.get(a.layerId ?? null) ?? 0) + 1);
  let best: { id: string | null; n: number } | undefined;
  for (const [id, n] of counts) if (!best || n > best.n) best = { id, n };
  return best?.id ?? null;
}

/** No layers and no connections: nobody has drawn it yet. */
export function isUndrawn(doc: Pick<ProjectArchitecturePut, 'layers' | 'connections'>): boolean {
  return (doc.layers?.length ?? 0) === 0 && (doc.connections?.length ?? 0) === 0;
}

/**
 * Which of a group's areas the picture draws: the code, plus any support
 * area that something points at (`always`: an end of a connection, the
 * selection). The rest wait behind "and N more".
 */
export function splitGroupAreas(areas: readonly ProjectArchArea[], always: ReadonlySet<string>, showAll: boolean): { shown: ProjectArchArea[]; hidden: number } {
  if (showAll) return { shown: [...areas], hidden: 0 };
  const shown = areas.filter((a) => !a.support || always.has(a.key));
  return { shown, hidden: areas.length - shown.length };
}

/** The connections an area or a whole repository takes part in. */
export function connectionsOf(model: Pick<ProjectArchModel, 'connections'>, target: { rootId: string; areaKey?: string }): ProjectArchConnection[] {
  const touches = (end: ProjectArchEnd) => end.known && end.rootId === target.rootId && (target.areaKey === undefined || end.areaKey === target.areaKey);
  return model.connections.filter((c) => touches(c.from) || touches(c.to));
}

// ── Editing: every helper returns a new document ───────────────────────

type Doc = ProjectArchitecturePut;

/** A document as a PUT takes it: no server stamps, no empty title, summary, description or note. */
export function toDocument(doc: Doc | ProjectArchitectureResponse): Doc {
  const { revision: _r, updatedAt: _a, updatedBy: _b, ...rest } = doc as ProjectArchitectureResponse;
  const { title, summary, layers, areas, connections, ...extra } = rest;
  const notes: Doc['areas'] = {};
  for (const [ref, note] of Object.entries(areas ?? {})) {
    const { title: t, description: d, layer: l, ...more } = note ?? {};
    const kept = { ...more, ...(t?.trim() ? { title: t.trim() } : {}), ...(d?.trim() ? { description: d.trim() } : {}), ...(l ? { layer: l } : {}) };
    if (Object.keys(kept).length) notes[ref] = kept;
  }
  return {
    ...extra,
    schemaVersion: 1,
    ...(title?.trim() ? { title: title.trim() } : {}),
    ...(summary?.trim() ? { summary: summary.trim() } : {}),
    layers: (layers ?? []).map(({ description, match, ...l }) => ({ ...l, ...(description?.trim() ? { description: description.trim() } : {}), match: [...(match ?? [])] })),
    areas: notes,
    connections: (connections ?? []).map(({ label, description, ...c }) => ({ ...c, ...(label?.trim() ? { label: label.trim() } : {}), ...(description?.trim() ? { description: description.trim() } : {}) })),
  };
}

function stable(value: unknown): string {
  if (Array.isArray(value)) return `[${value.map(stable).join(',')}]`;
  if (value && typeof value === 'object') {
    return `{${Object.entries(value as Record<string, unknown>)
      .filter(([, v]) => v !== undefined)
      .sort(([a], [b]) => (a < b ? -1 : a > b ? 1 : 0))
      .map(([k, v]) => `${JSON.stringify(k)}:${stable(v)}`)
      .join(',')}}`;
  }
  return JSON.stringify(value) ?? 'null';
}

/** Whether two documents would save the same thing. */
export function sameDocument(a: Doc | ProjectArchitectureResponse, b: Doc | ProjectArchitectureResponse): boolean {
  return stable(toDocument(a)) === stable(toDocument(b));
}

/** An id for a new layer from its title, unlike any taken. */
export function layerIdFor(title: string, taken: Iterable<string>): string {
  const used = new Set(taken);
  const base =
    title
      .toLowerCase()
      .normalize('NFKD')
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/^-+|-+$/g, '')
      .slice(0, 40) || 'layer';
  let id = base;
  for (let n = 2; used.has(id); n++) id = `${base}-${n}`;
  return id;
}

export function setHeading(doc: Doc, heading: { title?: string; summary?: string }): Doc {
  const next: Doc = { ...doc };
  if (heading.title !== undefined) {
    if (heading.title) next.title = heading.title;
    else delete next.title;
  }
  if (heading.summary !== undefined) {
    if (heading.summary) next.summary = heading.summary;
    else delete next.summary;
  }
  return next;
}

/** Add a layer at the bottom (or at `at`); answers the document and the new layer's id. */
export function addLayer(doc: Doc, layer: { title: string; description?: string; match?: string[] }, at?: number): { document: Doc; id: string } {
  const id = layerIdFor(layer.title, doc.layers.map((l) => l.id));
  const added: ProjectLayer = { id, title: layer.title.trim() || 'New layer', ...(layer.description?.trim() ? { description: layer.description.trim() } : {}), match: [...(layer.match ?? [])] };
  const layers = [...doc.layers];
  layers.splice(at === undefined ? layers.length : Math.max(0, Math.min(at, layers.length)), 0, added);
  return { document: { ...doc, layers }, id };
}

/** Rename or describe a layer; its id, and so everything placed in it, stays. */
export function updateLayer(doc: Doc, id: string, patch: { title?: string; description?: string }): Doc {
  return {
    ...doc,
    layers: doc.layers.map((l) => {
      if (l.id !== id) return l;
      const next = { ...l };
      if (patch.title !== undefined) next.title = patch.title;
      if (patch.description !== undefined) {
        if (patch.description) next.description = patch.description;
        else delete next.description;
      }
      return next;
    }),
  };
}

/** Remove a layer. What it held is no longer placed: the notes that named it forget it. */
export function removeLayer(doc: Doc, id: string): Doc {
  const areas: Doc['areas'] = {};
  for (const [ref, note] of Object.entries(doc.areas ?? {})) {
    if (note.layer !== id) {
      areas[ref] = note;
      continue;
    }
    const { layer: _l, ...rest } = note;
    if (Object.keys(rest).length) areas[ref] = rest;
  }
  return { ...doc, layers: doc.layers.filter((l) => l.id !== id), areas };
}

/** Move a layer up (`-1`) or down (`1`) the picture; a move off either end changes nothing. */
export function moveLayer(doc: Doc, id: string, by: number): Doc {
  const from = doc.layers.findIndex((l) => l.id === id);
  const to = from + by;
  if (from < 0 || to < 0 || to >= doc.layers.length || by === 0) return doc;
  const layers = [...doc.layers];
  const [layer] = layers.splice(from, 1);
  layers.splice(to, 0, layer);
  return { ...doc, layers };
}

const namesRepository = (entry: string, rootId: string, rootIds?: readonly string[]) => parseAreaRef(entry, rootIds).rootId === rootId;
const namesWholeRepository = (entry: string, rootId: string, rootIds?: readonly string[]) => {
  const m = parseAreaRef(entry, rootIds);
  return m.rootId === rootId && (m.path === '*' || m.path === '');
};

/**
 * Put a whole repository in a layer: `<rootId>:*` on that layer, and off
 * every other. Areas placed one by one (a note, a narrower entry) stay where
 * they were put. `undefined` takes the repository out of every layer.
 */
export function placeRepository(doc: Doc, rootId: string, layerId: string | undefined, rootIds?: readonly string[]): Doc {
  const whole = areaRefOf(rootId, '*');
  return {
    ...doc,
    layers: doc.layers.map((l) => {
      const match = (l.match ?? []).filter((e) => !namesWholeRepository(e, rootId, rootIds));
      if (l.id === layerId) match.push(whole);
      return match.length === (l.match ?? []).length && match.every((e, i) => e === l.match[i]) ? l : { ...l, match };
    }),
  };
}

/**
 * Move what the picture shows of one repository in one layer to another
 * layer: every entry of that repository on `from` goes to `to`, and the
 * notes that put an area in `from` now say `to`. From "Not placed yet"
 * (`from` undefined) that is the whole repository: `<rootId>:*` on `to`.
 * To `undefined`, the entries are dropped and the notes forget their layer.
 */
export function moveGroup(doc: Doc, rootId: string, from: string | undefined, to: string | undefined, rootIds?: readonly string[]): Doc {
  if (from === to) return doc;
  if (from === undefined) return to === undefined ? doc : placeRepository(doc, rootId, to, rootIds);
  const moving = (doc.layers.find((l) => l.id === from)?.match ?? []).filter((e) => namesRepository(e, rootId, rootIds));
  const layers = doc.layers.map((l) => {
    if (l.id === from) return { ...l, match: (l.match ?? []).filter((e) => !namesRepository(e, rootId, rootIds)) };
    if (l.id === to && moving.length) return { ...l, match: [...(l.match ?? []), ...moving.filter((e) => !(l.match ?? []).includes(e))] };
    return l;
  });
  const areas: Doc['areas'] = {};
  for (const [ref, note] of Object.entries(doc.areas ?? {})) {
    if (note.layer !== from || parseAreaRef(ref, rootIds).rootId !== rootId) {
      areas[ref] = note;
      continue;
    }
    const { layer: _l, ...rest } = note;
    if (to !== undefined) areas[ref] = { ...rest, layer: to };
    else if (Object.keys(rest).length) areas[ref] = rest;
  }
  return { ...doc, layers, areas };
}

/**
 * Put one area in a layer: its own note says so, which beats whatever its
 * repository's entries say. `undefined` forgets the note's layer, so the
 * area goes back to wherever its repository is.
 */
export function placeArea(doc: Doc, ref: string, layerId: string | undefined): Doc {
  const { layer: _l, ...rest } = doc.areas?.[ref] ?? {};
  const areas = { ...(doc.areas ?? {}) };
  if (layerId !== undefined) areas[ref] = { ...rest, layer: layerId };
  else if (Object.keys(rest).length) areas[ref] = rest;
  else delete areas[ref];
  return { ...doc, areas };
}

/** The project's own name and words for an area; empty strings give the repository's back. */
export function describeArea(doc: Doc, ref: string, patch: { title?: string; description?: string }): Doc {
  const note = { ...(doc.areas?.[ref] ?? {}) };
  if (patch.title !== undefined) {
    if (patch.title) note.title = patch.title;
    else delete note.title;
  }
  if (patch.description !== undefined) {
    if (patch.description) note.description = patch.description;
    else delete note.description;
  }
  const areas = { ...(doc.areas ?? {}) };
  if (Object.keys(note).length) areas[ref] = note;
  else delete areas[ref];
  return { ...doc, areas };
}

/** An end of a connection as the document writes it: `<rootId>` or `<rootId>:<path>`. */
export function connectionRef(rootId: string, path?: string): string {
  return path ? areaRefOf(rootId, path) : rootId;
}

/** A type, not an interface: it goes into the document's list, whose entries may carry fields of their own. */
export type ConnectionDraft = {
  from: string;
  to: string;
  kind: ProjectConnectionKind;
  label?: string;
  description?: string;
};

function cleanConnection<T extends ConnectionDraft>(c: T): T {
  const { label, description, ...rest } = c;
  return { ...rest, ...(label ? { label } : {}), ...(description ? { description } : {}) } as T;
}

export function addConnection(doc: Doc, connection: ConnectionDraft): Doc {
  return { ...doc, connections: [...(doc.connections ?? []), cleanConnection(connection)] };
}

export function updateConnection(doc: Doc, index: number, patch: Partial<ConnectionDraft>): Doc {
  if (!doc.connections?.[index]) return doc;
  return { ...doc, connections: doc.connections.map((c, i) => (i === index ? cleanConnection({ ...c, ...patch }) : c)) };
}

export function removeConnection(doc: Doc, index: number): Doc {
  if (!doc.connections?.[index]) return doc;
  return { ...doc, connections: doc.connections.filter((_, i) => i !== index) };
}

/** Why a connection cannot be added as it stands, in a sentence; undefined when it can. */
export function connectionProblem(c: Pick<ConnectionDraft, 'from' | 'to'>, rootIds: readonly string[]): string | undefined {
  if (!c.from || !c.to) return 'Choose the repository at each end.';
  const from = parseAreaRef(c.from, rootIds);
  const to = parseAreaRef(c.to, rootIds);
  if (!rootIds.includes(from.rootId) || !rootIds.includes(to.rootId)) return 'Both ends must be repositories of this project.';
  if (from.rootId === to.rootId) return 'A connection joins two different repositories. What happens inside one repository is on its own map.';
  return undefined;
}

/** A first picture: one layer per repository, each holding that whole repository. Kept title, summary, notes and connections. */
export function seedDocument(state: ProjectArchInput, doc: Doc = state.architecture): Doc {
  let next: Doc = { ...toDocument(doc), layers: [] };
  for (const repo of repositoriesOf(state)) next = addLayer(next, { title: repo.name, match: [areaRefOf(repo.rootId, '*')] }).document;
  return next;
}

// ── Suggestions ────────────────────────────────────────────────────────

/** Folder names that usually hold what two repositories agree on. */
const SHARED_SEGMENTS: Record<string, ProjectConnectionKind> = {
  schema: 'protocol',
  schemas: 'protocol',
  protocol: 'protocol',
  protocols: 'protocol',
  proto: 'protocol',
  contracts: 'protocol',
  api: 'protocol',
  shared: 'package',
  common: 'package',
  client: 'package',
  sdk: 'package',
};
/** One side has a client, the other something that serves. */
const CLIENT_SEGMENTS = new Set(['client', 'api-client', 'sdk']);
const SERVER_SEGMENTS = new Set(['api', 'http', 'routes', 'server', 'rest']);

export type ConnectionSuggestion = ConnectionDraft & {
  /** Stable for a list key. */
  id: string;
  /** Why it is suggested, in a sentence. */
  reason: string;
  fromCaption: string;
  toCaption: string;
};

const lastSegment = (path: string) => path.split('/').filter(Boolean).pop()?.toLowerCase() ?? '';

/**
 * Connections a person might want, read only from what the repositories
 * shared of their own maps: a client in one and something that serves in
 * another, or the same telling folder name (`schema`, `protocol`, `shared`…)
 * in both. Guesses: never added unless accepted, and none that the document
 * already has between the same two areas. At most `limit`.
 */
export function suggestConnections(state: ProjectArchInput, document: Doc = state.architecture, limit = 6): ConnectionSuggestion[] {
  const model = buildProjectArchModel(state, document);
  const rootIds = model.repositories.map((r) => r.rootId);
  const nameOf = new Map(model.repositories.map((r) => [r.rootId, r.name]));
  const have = new Set<string>();
  for (const c of model.connections) {
    const a = c.from.areaKey ?? c.from.rootId;
    const b = c.to.areaKey ?? c.to.rootId;
    have.add(`${a}|${b}`).add(`${b}|${a}`);
  }
  const bySegment = new Map<string, ProjectArchArea[]>();
  for (const area of model.areas.values()) {
    if (area.support) continue;
    const seg = lastSegment(area.path);
    if (seg) (bySegment.get(seg) ?? bySegment.set(seg, []).get(seg)!).push(area);
  }
  const out: ConnectionSuggestion[] = [];
  const offer = (from: ProjectArchArea, to: ProjectArchArea, kind: ProjectConnectionKind, reason: string) => {
    if (from.rootId === to.rootId || have.has(`${from.key}|${to.key}`)) return;
    have.add(`${from.key}|${to.key}`).add(`${to.key}|${from.key}`);
    out.push({ id: `${from.key}>${to.key}`, from: from.key, to: to.key, kind, reason, fromCaption: `${nameOf.get(from.rootId) ?? from.rootId} · ${from.title}`, toCaption: `${nameOf.get(to.rootId) ?? to.rootId} · ${to.title}` });
  };
  // A client on one side, something that serves on the other: the client calls it.
  for (const c of CLIENT_SEGMENTS) {
    for (const from of bySegment.get(c) ?? []) {
      for (const s of SERVER_SEGMENTS) {
        for (const to of bySegment.get(s) ?? []) offer(from, to, 'http', `${nameOf.get(from.rootId) ?? from.rootId} has a “${lastSegment(from.path)}” area and ${nameOf.get(to.rootId) ?? to.rootId} a “${lastSegment(to.path)}” area.`);
      }
    }
  }
  // The same telling name in two repositories: likely one contract or one package, kept in both.
  for (const [seg, kind] of Object.entries(SHARED_SEGMENTS)) {
    const list = [...(bySegment.get(seg) ?? [])].sort((a, b) => rootIds.indexOf(a.rootId) - rootIds.indexOf(b.rootId));
    for (let i = 0; i < list.length; i++) {
      for (let j = i + 1; j < list.length; j++) offer(list[i], list[j], kind, `Both repositories have a “${seg}” area.`);
    }
  }
  return out.slice(0, limit);
}

// ── Captions ───────────────────────────────────────────────────────────

/** Where the project's architecture is kept, said once. */
export function homeCaption(home: ProjectHomeKind): string {
  return home === 'organisation' ? 'Kept by your organisation' : home === 'hub' ? 'Kept on the hub' : 'Kept on this computer';
}

/** A person's name for an id in a stamp; the id itself is never shown. */
export function personName(principal: string | undefined, people: Record<string, { name: string }> | undefined): string | undefined {
  return principal ? people?.[principal]?.name?.trim() || undefined : undefined;
}

/** "Last changed by Sam via scribe, 2 h ago"; empty before the first save. */
export function lastChangedCaption(architecture: Pick<ProjectArchitectureResponse, 'revision' | 'updatedAt' | 'updatedBy'>, people: Record<string, { name: string }> | undefined, now: number = Date.now()): string {
  if (!architecture.revision && !architecture.updatedAt) return '';
  const who = personName(architecture.updatedBy?.principal, people);
  const actor = architecture.updatedBy?.actor?.trim();
  const when = relativeTime(architecture.updatedAt, now);
  const by = who ? ` by ${who}` : '';
  const via = actor ? ` via ${actor}` : '';
  if (!by && !via && !when) return '';
  return `Last changed${by}${via}${when ? `${by || via ? ',' : ''} ${when}` : ''}`;
}

/**
 * What a repository's group says when it has no areas to draw. One on this
 * computer is described by "Ask the Scribe" before the project is drawn
 * (desktop/src/main/projectScribe.ts); one elsewhere has to be opened there.
 */
export function emptyRepositoryText(repo: Pick<ProjectArchRepository, 'mirroredAt' | 'desktopRootId'> | undefined): string {
  if (repo?.mirroredAt) return 'No areas yet. Build its memory and they appear here.';
  return repo?.desktopRootId ? 'Not described yet. Ask the Scribe and it is described first.' : 'Nothing known yet. Open it on the computer that has it and it appears here.';
}

/** The quiet line while "Ask the Scribe" is still getting repositories described; empty when nothing waits. */
export function scribeWaitingCaption(scribe: ProjectWorkState['scribe'] | undefined, now: number = Date.now()): string {
  if (!scribe) return '';
  const names = scribe.waitingFor;
  const list = names.length <= 1 ? (names[0] ?? '') : `${names.slice(0, -1).join(', ')} and ${names[names.length - 1]}`;
  const what = scribe.what === 'roadmap' ? 'project roadmap' : 'project picture';
  const since = relativeTime(scribe.since, now);
  const when = since ? ` Asked ${since}.` : '';
  return names.length ? `The Scribe is describing ${list} first; the ${what} is drawn when ${names.length === 1 ? 'it finishes' : 'they finish'}.${when}` : `The Scribe is drawing the ${what}.${when}`;
}
